import maplibregl from 'maplibre-gl'
import './maplibre-navigation-control.css'
import { featureCollection, bbox, point } from '@turf/turf'
import {
  ChangeEvent,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'
import { Feature, GeoJsonProperties, Geometry, GeoJSON } from 'geojson'
import { PostcodeDistrict } from '@leadgen/models/client'
import Box from '@mui/material/Box'
import { useStateWithRef } from '../../../../hooks/shared/useStateWithRef'
import PmtilesMap from '../../../../maplibre/PmtilesMap'
import { districtCentroids } from '../../../../constants/districtCentroids'
import districtGeometries from '../../../../constants/districtGeometries.json'
import useStableCallback from '../../../../hooks/shared/useStableCallback'

const enableLpaOutline = false
type DistrictFeature = Feature<Geometry, { name: PostcodeDistrict }>
const EMPTY_FEATURE_COLLECTION: GeoJSON<Geometry, GeoJsonProperties> = {
  type: 'FeatureCollection',
  features: [] as DistrictFeature[],
}

const DEFAULT_CENTER: maplibregl.LngLatLike = [-2.6, 52.4]
const UK_BOUNDS: [maplibregl.LngLatLike, maplibregl.LngLatLike] = [
  [-12.779, 45.605],
  [5.924, 65.199],
]

export type DistrictMapHandles = {
  scrollToPostcode: (postcodeDistrict: PostcodeDistrict) => void
}
// eslint-disable-next-line react/display-name
const DistrictMap = forwardRef<
  DistrictMapHandles,
  {
    postcodeDistricts: PostcodeDistrict[]
    onChange: (event: ChangeEvent<HTMLInputElement>) => void
    onSelect?: (postcodeDistricts: PostcodeDistrict) => void
  }
>(({ postcodeDistricts, onChange, onSelect }, ref) => {
  const mapContainer = useRef<HTMLDivElement>(null)
  const currentHoveredDistrictRef = useRef<string>()
  const [, setMap, mapRef] = useStateWithRef<maplibregl.Map | null>(null)
  const selectedDistrictsRef = useRef<PostcodeDistrict[]>(postcodeDistricts)
  selectedDistrictsRef.current = postcodeDistricts
  const stableOnChange = useStableCallback(onChange)
  const stableOnSelect = useStableCallback(onSelect)
  const fitBoundsToDistricts = useCallback(
    (mapInstance: maplibregl.Map, districts: PostcodeDistrict[]) => {
      if (!mapInstance.isStyleLoaded()) {
        return false
      }
      if (!districts.length) {
        return true
      }
      const matchingDistricts = districts.map(district => districtCentroids[district])
      if (!matchingDistricts.length) {
        return false
      }
      const centroids = featureCollection(matchingDistricts.map(c => point(c)))
      const bboxData = bbox(centroids)
      const bboxPadding = 0.5
      const bounds: [maplibregl.LngLatLike, maplibregl.LngLatLike] = [
        [bboxData[0] - bboxPadding, bboxData[1] - bboxPadding], // southwest corner
        [bboxData[2] + bboxPadding, bboxData[3] + bboxPadding], // northeast corner
      ]
      mapInstance.fitBounds(bounds, { padding: 20 })
      return true
    },
    [],
  )
  const updateSelectedDistrictsSource = useCallback(() => {
    const mapInstance = mapRef.current
    if (!mapInstance) {
      return
    }
    const source = mapInstance.getSource('selected-districts') as
      | maplibregl.GeoJSONSource
      | undefined
    if (!source) {
      return
    }
    const features = selectedDistrictsRef.current
      .map(
        district =>
          (districtGeometries as unknown as Record<PostcodeDistrict, DistrictFeature>)[district] as
            | DistrictFeature
            | undefined,
      )
      .filter((feature): feature is DistrictFeature => Boolean(feature))
    source.setData({
      type: 'FeatureCollection',
      features,
    })
  }, [mapRef])

  const scrollToPostcode = useCallback(
    (postcodeDistrict: PostcodeDistrict) => {
      if (!mapRef.current) {
        return
      }
      mapRef.current.flyTo({
        center: districtCentroids[postcodeDistrict],
        zoom: 9,
        speed: 1.2,
        curve: 1,
        essential: true,
      })
    },
    [mapRef],
  )
  useImperativeHandle(ref, () => ({
    scrollToPostcode,
  }))

  useEffect(() => {
    if (mapRef.current || !mapContainer.current) {
      // stops map from intializing more than once
      return
    }
    const districtsSource = PmtilesMap.registerSource('districts.pmtiles')
    const gbSource = PmtilesMap.registerSource('gb.pmtiles')
    const placesSource = PmtilesMap.registerSource('places.pmtiles')
    const roadsSource = PmtilesMap.registerSource('roads.pmtiles')
    const localPlanningAuthoritiesSource = enableLpaOutline
      ? PmtilesMap.registerSource('localPlanningAuthorities.pmtiles')
      : null
    const mapInstance = new PmtilesMap({
      container: mapContainer.current,
      attributionControl: false,
      style: {
        version: 8,
        sources: {
          ...(enableLpaOutline
            ? {
                localPlanningAuthorities: {
                  type: 'vector',
                  url: localPlanningAuthoritiesSource as string,
                },
              }
            : {}),
          'selected-districts': {
            type: 'geojson',
            data: EMPTY_FEATURE_COLLECTION,
          },
          districts: {
            type: 'vector',
            url: districtsSource,
          },
          gb: {
            type: 'vector',
            url: gbSource,
          },
          places: {
            type: 'vector',
            url: placesSource,
          },
          roads: {
            type: 'vector',
            url: roadsSource,
          },
        },
        glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
        layers: [
          {
            id: 'gb-fill',
            type: 'fill',
            source: 'gb',
            'source-layer': 'gb',
            layout: {},
            paint: {
              'fill-color': '#e9f2e7', // Green color
            },
          },
          {
            id: 'motorways',
            type: 'line',
            source: 'roads',
            'source-layer': 'roads',
            minzoom: 6, // Only show when zoom level is 6 and higher
            maxzoom: 22, // Show up to the highest zoom level
            filter: ['==', ['get', 'type'], 'motorway'],
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': '#fff',
              'line-width': [
                'interpolate',
                ['linear'],
                ['zoom'],
                6,
                0.3, // At zoom level 6, line width will be 0.3
                7,
                2, // At zoom level 7+, line width will be 2
              ],
            },
          },
          {
            id: 'trunks',
            type: 'line',
            source: 'roads',
            'source-layer': 'roads',
            minzoom: 7, // Only show when zoom level is 6 and higher
            maxzoom: 22, // Show up to the highest zoom level
            filter: ['==', ['get', 'type'], 'trunk'],
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': '#fff',
              'line-width': 1,
            },
          },
          {
            id: 'primary',
            type: 'line',
            source: 'roads',
            'source-layer': 'roads',
            minzoom: 8, // Only show when zoom level is 6 and higher
            maxzoom: 22, // Show up to the highest zoom level
            filter: ['==', ['get', 'type'], 'primary'],
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': '#fff',
              'line-width': 1,
            },
          },
          // {
          //   id: 'secondary',
          //   type: 'line',
          //   source: 'roads',
          //   minzoom: 9, // Only show when zoom level is 6 and higher
          //   maxzoom: 22, // Show up to the highest zoom level
          //   filter: ['==', ['get', 'type'], 'secondary'],
          //   layout: {
          //     'line-join': 'round',
          //     'line-cap': 'round',
          //   },
          //   paint: {
          //     'line-color': 'orange',
          //     'line-width': 1,
          //   },
          // },
          {
            id: 'major-cities-labels',
            type: 'symbol',
            source: 'places',
            'source-layer': 'places',
            minzoom: 6, // Only show when zoom level is 6 and higher
            maxzoom: 22, // Show up to the highest zoom level
            filter: ['==', ['get', 'type'], 'major-city'],
            layout: {
              'text-field': ['get', 'name'], // Get the 'name' property from GeoJSON
              // 'text-font': ['Roboto Regular'],
              'text-size': 14, // Font size
              'text-anchor': 'center', // Position text at the center
              'text-justify': 'center',
              'symbol-sort-key': 0, // highest priority
              'text-optional': false, // force placement over lower-priority labels
              'text-padding': 4,
            },
            paint: {
              'text-color': '#b3aeae', // Text color
            },
          },
          {
            id: 'cities-labels',
            type: 'symbol',
            source: 'places',
            'source-layer': 'places',
            minzoom: 7, // Only show when zoom level is 6 and higher
            maxzoom: 22, // Show up to the highest zoom level
            filter: ['==', ['get', 'type'], 'city'],
            layout: {
              'text-field': ['get', 'name'], // Get the 'name' property from GeoJSON
              // 'text-font': ['Roboto Regular'],
              // 'text-size': 12, // Font size
              'text-size': [
                'interpolate',
                ['linear'],
                ['zoom'],
                7,
                12, // at zoom 8 → size 8
                12,
                17, // at zoom 10 → size 12
                // 14,
                // 18, // at zoom 14+ → size 18
              ],

              'text-anchor': 'center', // Position text at the center
              'text-justify': 'center',
              'symbol-sort-key': 1, // higher priority than towns/villages
              'text-optional': true, // doesnt have to be rendered if no space
              'text-padding': 2,
            },
            paint: {
              'text-color': '#b3aeae', // Text color
            },
          },
          {
            id: 'towns-labels',
            type: 'symbol',
            source: 'places',
            'source-layer': 'places',
            minzoom: 8, // Only show when zoom level is 7 and higher
            maxzoom: 22, // Show up to the highest zoom level
            filter: ['==', ['get', 'type'], 'town'],
            layout: {
              'text-field': ['get', 'name'], // Get the 'name' property from GeoJSON
              'text-size': [
                'interpolate',
                ['linear'],
                ['zoom'],
                8,
                8, // at zoom 8 → size 8
                12,
                14, // at zoom 12 → size 14
                // 14,
                // 18, // at zoom 14+ → size 18
              ],
              'text-anchor': 'center', // Position text at the center
              'text-justify': 'center',
              'symbol-sort-key': 1,
              'text-optional': true, // doesnt have to be rendered if no space
            },
            paint: {
              'text-color': '#b3aeae', // Text color
            },
          },
          {
            id: 'villages-labels',
            type: 'symbol',
            source: 'places',
            'source-layer': 'places',
            minzoom: 10, // Only show when zoom level is 6 and higher
            maxzoom: 22, // Show up to the highest zoom level
            filter: ['==', ['get', 'type'], 'village'],
            layout: {
              'text-field': ['get', 'name'], // Get the 'name' property from GeoJSON
              // 'text-font': ['Roboto Regular'],
              // 'text-size': 8, // Font size
              'text-size': [
                'interpolate',
                ['linear'],
                ['zoom'],
                10,
                8, // at zoom 10 → size 8
                12,
                12, // at zoom 12 → size 12
              ],
              'text-anchor': 'center', // Position text at the center
              'text-justify': 'center',
              'symbol-sort-key': 1, // lowest priority
              'text-optional': true, // doesnt have to be rendered if no space
            },
            paint: {
              'text-color': '#b3aeae', // Text color
            },
          },
          {
            id: 'districts-fill',
            type: 'fill',
            source: 'districts',
            'source-layer': 'districts',
            layout: {},
            paint: {
              'fill-color': '#d9ded6',
              'fill-opacity': 0.3, // Adjust opacity as needed
            },
          },
          {
            id: 'districts-selected-fill',
            type: 'fill',
            source: 'selected-districts',
            layout: {},
            paint: {
              'fill-color': '#008000', // Color when selected
              'fill-opacity': 0.3, // Adjust opacity as needed
            },
          },
          {
            id: 'districts-border',
            type: 'line',
            source: 'districts',
            'source-layer': 'districts',
            layout: {},
            paint: {
              'line-color': '#b3aeae', // Default color
              // 'line-width': 0.8,
              'line-width': [
                'interpolate',
                ['linear'],
                ['zoom'],
                6,
                0.1, // At zoom level 6, line width will be 0.3
                7,
                0.8, // At zoom level 7+, line width will be 2
              ],
            },
          },
          {
            id: 'districts-selected-border',
            type: 'line',
            source: 'selected-districts',
            layout: {},
            paint: {
              'line-color': '#769e75', // Color when selected
              'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.2, 7, 1.1],
            },
          },
          ...(enableLpaOutline
            ? [
                {
                  id: 'local-planning-authorities-border',
                  type: 'line',
                  source: 'localPlanningAuthorities',
                  'source-layer': 'localPlanningAuthorities',
                  layout: {},
                  paint: {
                    'line-color': '#008000', // Black color for the outline
                    // 'line-width': 0.8,
                    'line-width': [
                      'interpolate',
                      ['linear'],
                      ['zoom'],
                      6,
                      0.2, // At zoom level 6, line width will be 0.3
                      7,
                      1, // At zoom level 7+, line width will be 2
                    ],
                  },
                } as maplibregl.LayerSpecification,
              ]
            : []),
        ],
      },
      zoom: 7,
      center: DEFAULT_CENTER, // zoom in on Devon
      minZoom: 4, // Minimum zoom level
      maxZoom: 12, // Maximum zoom level
      maxBounds: UK_BOUNDS,
    })
    setMap(mapInstance)
    mapInstance.on('load', () => {
      updateSelectedDistrictsSource()
      if (!fitBoundsToDistricts(mapInstance, selectedDistrictsRef.current)) {
        const retryFit = () => {
          if (fitBoundsToDistricts(mapInstance, selectedDistrictsRef.current)) {
            mapInstance.off('idle', retryFit)
          }
        }
        mapInstance.on('idle', retryFit)
      }
    })
    // @ts-expect-error districtMap is a global variable
    window.districtMap = mapInstance
    mapInstance.on(
      'click',
      'districts-fill',
      (
        e: maplibregl.MapMouseEvent & {
          features?: maplibregl.MapGeoJSONFeature[] | undefined
        },
      ) => {
        const selectedFeature = e.features?.[0]
        if (!selectedFeature) {
          return
        }
        const selectedName = selectedFeature.properties.name as PostcodeDistrict
        const indexOfSelectedDistrict = selectedDistrictsRef.current.indexOf(selectedName)
        if (indexOfSelectedDistrict === -1) {
          selectedDistrictsRef.current = [...selectedDistrictsRef.current, selectedName]
        } else {
          selectedDistrictsRef.current = [
            ...selectedDistrictsRef.current.slice(0, indexOfSelectedDistrict),
            ...selectedDistrictsRef.current.slice(indexOfSelectedDistrict + 1),
          ]
        }
        updateSelectedDistrictsSource()
        stableOnChange({
          target: { value: selectedName },
        } as React.ChangeEvent<HTMLInputElement>)
        if (stableOnSelect) {
          stableOnSelect(selectedName)
        }
      },
    )
    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
    })
    mapInstance.on('mouseenter', 'districts-fill', function () {
      mapInstance.getCanvas().style.cursor = 'pointer'
    })
    mapInstance.on(
      'mousemove',
      'districts-fill',
      function (
        e: maplibregl.MapMouseEvent & {
          features?: maplibregl.MapGeoJSONFeature[] | undefined
        },
      ) {
        const properties = e.features?.[0].properties
        if (properties) {
          // If the hovered feature is different, update the popup
          if (currentHoveredDistrictRef.current !== properties.name) {
            currentHoveredDistrictRef.current = properties.name as PostcodeDistrict
            popup
              .setLngLat(e.lngLat)
              .setHTML(`<strong>${properties.name}</strong><br>`)
              .addTo(mapInstance)
          } else {
            popup.setLngLat(e.lngLat)
          }
        }
      },
    )
    // Change the cursor back to default when it leaves the feature layer
    mapInstance.on('mouseleave', 'districts-fill', function () {
      mapInstance.getCanvas().style.cursor = ''
      currentHoveredDistrictRef.current = undefined
      popup.remove()
    })

    mapInstance.addControl(new maplibregl.NavigationControl(), 'top-right')
  }, [
    mapRef,
    stableOnChange,
    stableOnSelect,
    setMap,
    updateSelectedDistrictsSource,
    fitBoundsToDistricts,
  ])
  useEffect(() => {
    const mapInstance = mapRef.current
    return () => {
      mapInstance?.remove()
      // @ts-expect-error districtMap is a global variable
      window.districtMap = undefined
    }
  }, [mapRef])
  useEffect(() => {
    selectedDistrictsRef.current = postcodeDistricts
    updateSelectedDistrictsSource()
  }, [postcodeDistricts, updateSelectedDistrictsSource])
  return (
    <Box sx={{ position: 'relative', display: 'flex', flex: 1 }}>
      <Box
        ref={mapContainer}
        style={{
          flex: 1,
        }}
      />
    </Box>
  )
})

export default memo(DistrictMap)
